import React from 'react';
import PropTypes from 'prop-types';
import { Tabs } from './Tabs';
import { getIds, useTabContext } from './TabControllerContext';

/**
 * `TabList` is a wrapper for `Tab`s to more easily implement fully accessible tabs.
 * It is meant to be used inside a `TabController` component.
 *
 * Related components: [TabController](#tabcontroller), [TabPanel](#tabpanel), [Tab](#tab)
 *
 * Usage:
 *
 * ```jsx
 * import { TabList } from '@one-thd/sui-atomic-components';
 * ```
 */
const TabList = React.forwardRef((props, ref) => {

  const { children: childrenProp, ...other } = props;
  const context = useTabContext();
  if (context === null) {
    throw new TypeError('No TabController provided');
  }
  const children = React.Children.map(childrenProp, (child) => {
    if (!React.isValidElement(child)) {
      return null;
    }

    const { tabId, panelId } = getIds(context, child.props.value) || {};

    return React.cloneElement(child, {
      'aria-controls': panelId,
      id: tabId,
    });
  });

  return (
    <Tabs {...other} ref={ref} value={context.value}>
      {children}
    </Tabs>
  );
});

TabList.displayName = 'TabList';

TabList.propTypes = {
  /**
   * The content of the component.
   */
  children: PropTypes.node
};

export { TabList };
